// ─────────────────────────────────────────────────────────
// components/ErrorBoundary.tsx — Phase 10
// Class-based error boundary that catches render errors in
// any subtree. Renders a recovery UI instead of a blank screen.
// Usage:
//   <ErrorBoundary><SomePage /></ErrorBoundary>
//   <ErrorBoundary fallback={<div>Custom fallback</div>}>…</ErrorBoundary>
// ─────────────────────────────────────────────────────────
import { Component, type ReactNode, type ErrorInfo } from 'react'
import { sentryCapture } from '@/lib/sentry'

interface Props {
  children:  ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  message:  string
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, message: '' }
  }

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err?.message ?? 'Unknown error' }
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', err, info.componentStack)
    sentryCapture(err, { componentStack: info.componentStack })
  }

  handleReset = () => this.setState({ hasError: false, message: '' })

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.props.fallback) return this.props.fallback

    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: 300, padding: '40px 24px',
        color: 'var(--color-tx)',
      }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
          Something went wrong
        </div>
        <div style={{
          fontSize: 12, color: 'var(--color-tx3)',
          marginBottom: 20, maxWidth: 400, textAlign: 'center',
          fontFamily: 'monospace', background: 'var(--color-bg2)',
          padding: '8px 12px', borderRadius: 6,
        }}>
          {this.state.message}
        </div>
        <button
          onClick={this.handleReset}
          style={{
            padding: '8px 20px', background: 'var(--color-primary)',
            color: '#fff', border: 'none', borderRadius: 6,
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    )
  }
}
